// ADENDA z65 (Diego, 30-oct-2026) · bloque B: «Sitios del proyecto». Antes las otras sucursales solo se alcanzaban por enlaces de
// texto sin tipo, tarifa, estado ni PDF. Ahora cada sitio sale con su tipo, su tarifa y su estado, y dos botones que se usan con
// el dedo: «Editar» (sus recibos) y «PDF» (baja SU último PDF individual; si no tiene, lleva a su pestaña PDF).
// Candado 3: el botón baja solo una versión de ESA cotización (pdfIndividualDe: cotizacion_id === sitio), con el folio de su PDF.
// Verificador del 30-oct (medido a 375 y 1440 px): la lista heredaba la fila con salto de los enlaces cortos de la franja; a 375
// la franja medía 707 px de 812 (el subidor de Recibos bajo el pliegue), los botones caían a media frase y, a 1440, dos sitios
// compartían renglón con el Editar/PDF de uno pegado al enlace del otro. Ahora (estilos/sitios-proyecto.css): un sitio por
// renglón con separador, sus botones en su propio renglón y con el nombre del sitio; y la franja se PLIEGA: en el celular nace
// cerrada (una línea: «Sitios del proyecto (N)») y en la computadora abierta si son pocos (ABIERTA_SOLA_HASTA). Lo demás de la franja (PDF de todas, agregar
// sucursal) va dentro. Medido en pruebas-e2e/sitios-proyecto.e2e.ts.
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { FileDown, Pencil } from 'lucide-react';
import { ETIQUETA_ESTADO } from '@cotizador/compartido';
import type { ResumenCotizacion, ResumenVersion } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { Enlace, navegar } from '../../ruteo.tsx';
import { Boton } from '../../componentes/ui.tsx';
import { descargarComo } from '../../componentes/archivos.tsx';
import { pdfIndividualDe, textoSitio } from '../../logica/sitios.ts';
import '../../estilos/sitios-proyecto.css';

const cerrada = (c: Pick<ResumenCotizacion, 'estado'>) => c.estado === 'cancelada' || c.estado === 'perdida';

/** Verificador del 30-oct (2.ª vuelta, medido a 1440 con 12 sitios): abierta sola, la lista medía 1,439 px y empujaba el subidor de
 *  Recibos más de dos pantallas en CADA sucursal; y el plegado no se recordaba (al tocar «Editar» de otro sitio se volvía a abrir).
 *  Ahora se abre sola solo en la computadora (≥ 48rem, el corte de pantallas.css) y con pocos sitios; lo que el vendedor elige
 *  (abrir o plegar) se recuerda por proyecto mientras navega, y en la pestaña si la recarga. */
export const ABIERTA_SOLA_HASTA = 4;
const elegidas = new Map<string, boolean>();
const CLAVE = (grupo: string) => `sitios-proyecto:${grupo}`;
function elegidaPara(grupo: string): boolean | null {
  const ya = elegidas.get(grupo);
  if (ya !== undefined) return ya;
  try {
    const v = window.sessionStorage.getItem(CLAVE(grupo));
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}
function recordar(grupo: string, abierta: boolean): void {
  elegidas.set(grupo, abierta);
  try {
    window.sessionStorage.setItem(CLAVE(grupo), abierta ? '1' : '0');
  } catch {
    /* sin almacenamiento (ventana privada): se recuerda mientras no se recargue */
  }
}
/** Sin matchMedia (pruebas): como en la computadora. */
export function abiertaPorOmision(sitios: number): boolean {
  let compu = true;
  try {
    compu = typeof window === 'undefined' || typeof window.matchMedia !== 'function' || window.matchMedia('(min-width: 48rem)').matches;
  } catch {
    compu = true;
  }
  return compu && sitios <= ABIERTA_SOLA_HASTA;
}

/** ADENDA z67 (Diego, 30-oct-2026; etapa 2 de MULTISITIO-POR-SITIO): `todoEnUno` son los botones del PDF de todas (sacarlo y
 *  bajar el último). Van ARRIBA de la lista, lo primero al abrir «Sitios del proyecto»: con 15 sitios quedaban abajo de todos. */
export function SitiosDelProyecto({ sitios, actualId, vuelta, children, todoEnUno }: { sitios: readonly ResumenCotizacion[]; actualId: string; vuelta: number; children?: ReactNode; todoEnUno?: ReactNode }) {
  const [pdfs, setPdfs] = useState<Record<string, ResumenVersion | null>>({});
  const grupo = sitios.find((c) => c.multisitio)?.multisitio?.grupo_id ?? actualId;
  const [elegida, setElegida] = useState<boolean | null>(() => elegidaPara(grupo));
  const abierta = elegida ?? elegidaPara(grupo) ?? abiertaPorOmision(sitios.length);
  const clave = sitios.map((c) => `${c.id}:${c.version_actual}`).join(',');
  useEffect(() => {
    let vivo = true;
    void Promise.all(sitios.map(async (c) => [c.id, await api.detalle(c.id).then((d) => pdfIndividualDe(c.id, d.versiones)).catch(() => null)] as const)).then((xs) => {
      if (vivo) setPdfs(Object.fromEntries(xs));
    });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, vuelta]);
  return (
    <details className="sitios-proyecto" open={abierta}>
      <summary
        className="sitios-proyecto__titulo"
        onClick={(e) => {
          // El vendedor manda: se controla aquí (y no con el toggle nativo, que también salta cuando cambia el número de sitios).
          e.preventDefault();
          recordar(grupo, !abierta);
          setElegida(!abierta);
        }}
      >
        <strong>Sitios del proyecto{sitios.length > 0 ? ` (${sitios.length})` : ''}</strong>
        {!abierta && <span className="nota-suave">Toca para bajar el todo en uno, editar un sitio, bajar su PDF o agregar otro</span>}
      </summary>
      {todoEnUno && (
        <div className="sitios-proyecto__todo" role="group" aria-label="Todo en uno (PDF de todas las sucursales)">
          {todoEnUno}
        </div>
      )}
      {sitios.length > 0 && (
        <ul className="sitios-proyecto__lista" aria-label="Sucursales del proyecto">
          {sitios.map((c) => {
            const v = pdfs[c.id] ?? null;
            const nombre = c.multisitio?.sitio ?? c.folio;
            return (
              <li key={c.id} className="sitios-proyecto__sitio" aria-current={c.id === actualId ? 'page' : undefined}>
                <div className="sitios-proyecto__datos">
                  <span className="sitios-proyecto__nombre">
                    {c.id === actualId ? (
                      <strong>
                        {nombre} · {c.folio} (esta)
                      </strong>
                    ) : (
                      <Enlace a={`/cotizaciones/${c.id}`}>
                        {nombre} · {c.folio}
                      </Enlace>
                    )}
                  </span>
                  <span className="nota-suave">
                    {textoSitio(c, ETIQUETA_ESTADO[c.estado])}
                    {cerrada(c) && ` (${ETIQUETA_ESTADO[c.estado].toLowerCase()}: no va en el PDF de todas)`}
                  </span>
                </div>
                <div className="sitios-proyecto__botones" role="group" aria-label={`Botones de «${nombre}»`}>
                  <Boton chico variante="secundario" icono={<Pencil aria-hidden />} aria-label={`Editar «${nombre}»`} onClick={() => navegar(`/cotizaciones/${c.id}/${c.tipo ? 'recibos' : 'cuestionario'}`)}>
                    Editar
                  </Boton>
                  <Boton
                    chico
                    variante="secundario"
                    icono={<FileDown aria-hidden />}
                    aria-label={v?.pdf ? `PDF ${v.pdf.folio_pdf} de «${nombre}»` : `PDF de «${nombre}»`}
                    onClick={() => (v?.pdf ? void descargarComo(v.pdf.archivos.pdf, `${v.pdf.folio_pdf} ${c.cliente_nombre} ${nombre}.pdf`) : navegar(`/cotizaciones/${c.id}/pdf`))}
                  >
                    {v?.pdf ? `PDF ${v.pdf.folio_pdf}` : 'PDF'}
                  </Boton>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {children && <div className="sitios-proyecto__resto">{children}</div>}
    </details>
  );
}
